import { FC, Ref, useState } from 'react';

import classNames from 'classnames';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';

import { ReservationMode, ScheduleWorkingHours, TreezStore } from '@/types';
import { useFeaturedFlag, useGapCommerceStore, useStoreHours } from '@/hooks';
import {
  filterResModAndProviderStoreId,
  getStoreScheduleInfo,
  getTimeZoneAbbreviation,
  resolveTimeZone,
} from '@/utils';
import {
  Close,
  IconSvg,
  StoreCustomerTypeBadges,
  StoreStatusIndicator,
} from '@/components';

import styles from './treezstore.module.scss';
import { usePopper } from 'react-popper';

export const CURBSIDE_NOTICE_LABEL = 'Curbside pickup available';

export const CurbsideNotice: FC<{ className?: string; color?: string }> = ({
  className,
  color,
}) => (
  <span
    className={classNames(styles.store__curbside, className)}
    style={color ? { color } : undefined}
  >
    <IconSvg color="currentColor" name="car" />
    {CURBSIDE_NOTICE_LABEL}
  </span>
);

interface IStoreRadioItemProps {
  store: TreezStore;
  isSelected?: boolean;
  isOpen?: boolean;
  onSelected: () => {};
  hiddeStatus?: boolean;
  hideCurbside?: boolean;
}

const StoreRadioItem: FC<IStoreRadioItemProps> = ({
  store,
  isSelected,
  onSelected,
  hiddeStatus,
  hideCurbside,
}) => {
  const { store: gcStore } = useGapCommerceStore();
  const { isCurbsideAllowedForStore } = useFeaturedFlag();
  const [button, setButton] = useState<HTMLButtonElement | undefined>();
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);

  const { styles: popperStyles, attributes } = usePopper(button, panel, {
    modifiers: [
      {
        name: 'offset',
        options: { offset: [16, 12] },
      },
    ],
    placement: 'top-end',
  });

  const pickup_schedules = gcStore?.pickup_schedules?.filter(
    s => s?.stores_provider_id === store?.short_name,
  );

  const storeLabel = `${store.name} - ${store.city}, ${store.state}`;

  const closures = gcStore?.closures?.filter(s =>
    filterResModAndProviderStoreId(
      s,
      store?.short_name,
      ReservationMode.Pickup,
    ),
  );

  //const acceptingOrdersLabel = 'ACCEPTING ORDERS';

  const { isStoreOpen, storeStatusLabel, closureNote, isTemporarilyClosed } =
    useStoreHours({
      workingHours: store?.working_hours,
      gcWorkingHours: pickup_schedules?.[0]
        ?.working_hours as ScheduleWorkingHours[],
      closures: closures as any,
      timezone: resolveTimeZone(store?.dispensary_time_zone),
    });

  const storeSchedule = getStoreScheduleInfo(
    pickup_schedules?.[0]?.working_hours as ScheduleWorkingHours[],
  );

  const storeTzAbbr = getTimeZoneAbbreviation(
    resolveTimeZone(store?.dispensary_time_zone),
  );

  const hasCurbside =
    !hideCurbside && isCurbsideAllowedForStore(store?.short_name);

  const radioId = `store-radio-${store.short_name}`;

  return (
    <div className={styles.store_radio_item}>
      <label className={styles.store_radio_header} htmlFor={radioId}>
        <input
          aria-label={`Select store: ${storeLabel}`}
          checked={isSelected}
          className={styles.store_radio}
          id={radioId}
          onChange={onSelected}
          type="radio"
        />
        <span>{storeLabel}</span>
      </label>
      <div className={styles.store_content}>
        <div className={styles.store_content_address_container}>
          <span
            aria-label={`Store address: ${store.full_address}`}
            className={styles.store_content_address}
            role="group"
          >
            {store.full_address.split(',')[0]}
          </span>
          <StoreCustomerTypeBadges store={store} />
        </div>
        {!hiddeStatus && (
          <div className={styles.store__status}>
            <StoreStatusIndicator
              className={styles.store__status_content}
              fallbackLabel="Hours Not Available"
              isOpen={isStoreOpen}
              messageClassName={styles.store__status_content__message}
              statusLabel={storeStatusLabel}
            />
            <div className={styles.store__status_info}>
              {/*   {!isStoreOpen && !isTemporarilyClosed && (
                <Badge
                  aria-live="polite"
                  className={styles.store__status_info_badge}
                  role="status"
                >
                  {acceptingOrdersLabel}
                </Badge>
              )} */}
              <Popover className={styles.store__status_info__help}>
                <PopoverButton
                  aria-label="Show info"
                  ref={setButton as Ref<HTMLButtonElement>}
                >
                  <IconSvg name="alert-info" />
                </PopoverButton>
                <PopoverPanel
                  className={styles.store__status_info__help_body}
                  ref={setPanel as Ref<HTMLDivElement>}
                  style={{
                    ...popperStyles.popper,
                    zIndex: 9999,
                    maxWidth: '350px',
                  }}
                  {...attributes.popper}
                >
                  {({ close }) => (
                    <div className={styles.info__help_body}>
                      <div className={styles.peak}></div>
                      <div className={styles.popover_header}>
                        <h6 className={styles.popover_header__title}>
                          {isTemporarilyClosed
                            ? closureNote?.title ?? 'Temporarily Closed'
                            : `Store Hours${
                                storeTzAbbr ? ` (${storeTzAbbr})` : ''
                              }`}
                        </h6>
                        <Close
                          aria-label="Close store hours information"
                          className={styles?.popover_header__close_btn}
                          onClick={e => {
                            e.stopPropagation();
                            close();
                          }}
                        />
                      </div>
                      <div className={styles.popover_body}>
                        {isTemporarilyClosed ? (
                          <p>{closureNote?.message}</p>
                        ) : (
                          <>
                            {storeSchedule?.length > 0
                              ? storeSchedule?.map((el, index) => {
                                  return (
                                    <div
                                      className={
                                        styles.popover_body_store_schedule_container
                                      }
                                      key={`${el?.day}_${index}`}
                                    >
                                      <span>{el?.day}</span>
                                      <span>{el?.period}</span>
                                    </div>
                                  );
                                })
                              : 'This store hasn’t provided its operating hours yet.'}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </PopoverPanel>
              </Popover>
            </div>
          </div>
        )}
        {hasCurbside && <CurbsideNotice />}
      </div>
    </div>
  );
};

export { StoreRadioItem };
